/* eslint-disable max-lines-per-function */
import './CoralFAQ.scss'

import { useStickyBarHeight } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCoralWebFaq } from '@websrc/DataMappers/mapSpot'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import { ChevronArrowIcon } from '../../../coral-library/icons/ChevronArrow.icon'
type CoralFAQProps = ReturnType<typeof mapCoralWebFaq> & { renderCoralSpotSpacing?: boolean }

export const CoralFAQ: FC<CoralFAQProps> = ({
  header,
  description,
  trackingId,
  faq_items,
  renderCoralSpotSpacing,
}) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  // Keeps track of which FAQ items are open
  const [openIndexes, setOpenIndexes] = useState<number[]>([])
  // Keeps track of the heights of the FAQ answers
  const [heights, setHeights] = useState<number[]>([])

  // Store references to the answer divs
  const answerRefs = useRef<(HTMLDivElement | null)[]>([])

  // Get the height of the sticky bar
  const stickyBarHeight = useStickyBarHeight()

  // Function to calculate the heights of the answer divs
  const calculateHeights = () => {
    const newHeights = answerRefs.current.map((ref) => ref?.scrollHeight || 0)
    setHeights(newHeights)
  }

  // Function to handle the click event on an FAQ item
  const handleItemClick = (index: number) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  // Effect to recalculate heights when FAQ items or open indexes change
  useEffect(() => {
    calculateHeights()
  }, [faq_items, openIndexes])

  // Effect to handle window resize events
  useEffect(() => {
    const handleResize = () => {
      calculateHeights()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Effect to inject JSON-LD structured data into the head of the document
  useEffect(() => {
    // Generate JSON-LD structured data for the FAQ items. Makes the FAQ items eligible for rich results in Google Search
    const generateStructuredData = () => {
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq_items.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }

      return JSON.stringify(structuredData)
    }

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = generateStructuredData()
    document.head.appendChild(script)

    // Clean up the script when the component unmounts or updates
    return () => {
      document.head.removeChild(script)
    }
  }, [faq_items])

  return (
    <div
      className={`container-coral-spot ${
        renderCoralSpotSpacing ? '' : 'm--top-spacing-100 m--bottom-spacing-100'
      }`}
    >
      <div className={'faq'} data-t-id={trackingId}>
        {/* Main text section */}
        <div
          className={'faq__main-text'}
          style={{
            top: `${stickyBarHeight + 60}px`,
          }}
        >
          <h2 className={'faq__header'}>{header}</h2>
          {description && (
            <Markdown
              className={'faq__description'}
              markdown={description}
              onClick={(e) => {
                const target = e.target as HTMLElement
                if (target.tagName === 'A') {
                  matomo.trackEvent(
                    'engagement',
                    matomoPageTemplate,
                    ['FAQ', 'header', target.textContent || ''],
                    ['Description', description]
                  )
                }
              }}
            />
          )}
        </div>
        {/* FAQ list section */}
        <div className={'faq__items-container'}>
          {faq_items?.map((item, index) => {
            const isOpen = openIndexes.includes(index)
            return (
              <div className={'faq__item'} data-t-id={item.trackingId} key={index}>
                <input
                  checked={isOpen}
                  className={'faq__checkbox'}
                  id={`faq-checkbox-${index}`}
                  readOnly={true}
                  type={'checkbox'}
                />
                <label
                  className={'faq__question-container'}
                  htmlFor={`faq-checkbox-${index}`}
                  onClick={() => {
                    handleItemClick(index)
                    matomo.trackEvent('engagement', matomoPageTemplate, ['faq'], [item.question])
                  }}
                >
                  <p className={'faq__question'}>{item.question}</p>
                  <div className={'faq__question-icon-container'}>
                    <ChevronArrowIcon
                      className={'faq__question-icon'}
                      direction={isOpen ? 'up' : 'down'}
                    />
                  </div>
                </label>
                <div
                  className={'faq__answer-wrapper'}
                  style={{ maxHeight: isOpen ? `${heights[index] + 24}px` : '0' }}
                >
                  <div
                    className={'faq__answer-container'}
                    ref={(el) => (answerRefs.current[index] = el)}
                  >
                    <Markdown
                      className={'faq__answer'}
                      markdown={item.answer}
                      onClick={(e) => {
                        const target = e.target as HTMLElement
                        if (target.tagName === 'A') {
                          matomo.trackEvent(
                            'engagement',
                            matomoPageTemplate,
                            ['FAQ', 'answer', target.textContent || ''],
                            ['Question', item.question]
                          )
                        }
                      }}
                      skipWidth={true}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
